
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component, ObjectManager } from '../../../concepts/objects';
import { LocalStorageService, StorageService } from '../../../platform/StorageService';
import { Colors } from '../../../ui';
import { BoardInfo } from '../../canvas/BoardInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { ParticipantStripInfo } from '../../canvas/participants/ParticipantStripInfo';
import { PawnManager } from '../../canvas/workspace/participants/PawnManager';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { ActiveObject, resolve } from '../../Container';
import { alert } from '../alerts/AlertCommands';
import { Audited } from '../auditing/Audited';
import { ConcealmentIndex } from '../conceal/ConcealmentIndex';
import { AutoSpotlightInfo } from '../spotlight/SpotlightProperties';
import { ToastContext, ToastHub } from '../toast/ToastHub';
import { CreatedIncognitoAttr } from './IncognitoAssociations';
import { IncognitoInfo } from './IncognitoInfo';
import { IncognitoTracker } from './IncognitoTracker';
import { IncognitoWidget } from './IncognitoWidget';

export class IncognitoMechanics extends ActiveObject {

    @resolve(ConcealmentIndex) private concealIndex!: ConcealmentIndex;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(PawnManager) private pawns!: PawnManager;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(LocalStorageService) private tempData!: StorageService;
    @resolve(ToastHub) private toast!: ToastHub;
    @resolve(WidgetManager) private widgets!: WidgetManager;

    private tracker = null as IncognitoTracker;

    public start(): void {
        this.tracker = new IncognitoTracker(this.tempData, this.session.get(BoardInfo).id);
        this.sync();
        this.burden(
            this.pawns.observe(() => this.realizePawnOverridesIfApplicable()),
            this.session.observe(IncognitoInfo, this.sync),
            this.session.observe(AutoSpotlightInfo, this.cancelSpotlightWhenIncognito),
            this.toast.on('compile', this.patchToastContextIfApplicable),
            () => this.realizeIncognitoTrigger(false),
            () => this.realizeHideUserInterceptorIfApplicable(false),
        );
    }

    private sync = () => {
        this.realizePawnOverridesIfApplicable();
        this.realizeIncognitoTrigger();
        this.realizeHideUserInterceptorIfApplicable();
        this.realizeWidgetIfApplicable();
        this.setParticipantStripVisibility();
        this.cancelSpotlightWhenIncognito();
    };

    private patchToastContextIfApplicable = ([type, context]: [string, ToastContext]) => {
        if (!this.session.get(IncognitoInfo).enabled) return;
        context.senderName = 'Someone';
        context.userName = 'Someone';
    };

    private realizePawnOverridesIfApplicable = (force?: boolean) => {
        const realize = force ?? this.session.get(IncognitoInfo).enabled;
        if (realize) {
            this.pawns.forEach(p => {
                p.mutate({
                    overrides: {
                        color: Colors.observe,
                        imageUrl: 'deqo://icons/i8/qmark',
                        hat: 'Bag',
                    }
                });
            });
        }
        else {
            this.pawns.forEach(p => p.mutate({ overrides: {} }));
        }
    };

    private realizeIncognitoTrigger = (force?: boolean) => {
        const realize = force ?? this.session.get(IncognitoInfo).enabled;
        this.objects.deleteTrigger('Incognito.SetAssocation');
        if (realize) {
            this.objects.createTrigger('Incognito.SetAssocation')
                .forAllTypes()
                .onCreated()
                .doForEach((o: Component) => {
                    const a = o.as(Audited);
                    if (a) CreatedIncognitoAttr.set(a, true);
                });
        }
    };

    private realizeHideUserInterceptorIfApplicable = (force?: boolean) => {
        const realize = force ?? this.session.get(IncognitoInfo).enabled;
        const wasRealized = this.objects.hasInterceptor('Incognito.HideUser');
        if (realize === wasRealized) return;

        if (wasRealized) {
            this.objects.deleteInterceptor('Incognito.HideUser');
        }
        if (realize) {
            this.objects.createInterceptor('Incognito.HideUser')
                .forAllTypes()
                .on('userId', {
                    get: (o, v) => {
                        return v || this.tracker.get(o.id) || null;
                    },
                    set: (o, v) => {
                        this.tracker.set(o.id, v);
                        return null;
                    },
                });
        }

        // Force rerender of all incognito objects using touch
        const subjects = Array.from(this.objects.getAll(Audited))
            .filter(x => CreatedIncognitoAttr.get(x));
        this.objects.touch(subjects);
    };

    private realizeWidgetIfApplicable = () => {
        const { widgets } = this;

        widgets.delete('incognito');

        const incognito = this.session.get(IncognitoInfo);
        if (!incognito.enabled) return;

        const container = widgets.createContainer({
            name: 'incognito',
            weight: 100,
            style: 'none',
            position: ['docked', 'topRight', 'inline'],
        });

        container.add(widgets.createInline({
            ui: IncognitoWidget,
        }));

        widgets.add(container);
    };

    private cancelSpotlightWhenIncognito = () => {
        const spotlight = this.session.get(AutoSpotlightInfo);
        const incognito = this.session.get(IncognitoInfo);

        if (incognito.enabled && spotlight.enabled) {
            this.session.update(AutoSpotlightInfo, AutoSpotlightInfo.create(false));
            this.dispatch(alert, ['Spotlight is not available during Anonymous Mode.']);
        }
    };

    private setParticipantStripVisibility = () => {
        const incognito = this.session.get(IncognitoInfo);
        this.session.patch(ParticipantStripInfo, { enabled: !incognito.enabled });
    };
}
